import {
  Component,
  ViewEncapsulation,
  Injector,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
  IIrembogovBasicLabelKeyPair,
  alphaNanoIdUtil,
} from '@irembo-andela/irembogov3-common';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import { EDragDropItemsIds } from '../../../../core/models/form-builder-drag-item-ids.enum';
import { ValidationTypeDefaultMessage } from '../../../../core/utils/form-fields-formly-configurations/form-builder-validation-type-message.utils';

@Component({
  selector: 'irembogov-form-field-types-list',
  templateUrl: './form-field-types-list.component.html',
  styleUrls: ['./form-field-types-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FormFieldTypesListComponent implements OnInit, OnDestroy {
  EDragDropItemsIds = EDragDropItemsIds;
  randomKey: string = alphaNanoIdUtil(5);

  formFieldTypes: IIrembogovBasicLabelKeyPair<FormlyFieldConfig>[] = [
    {
      label: 'Attachment',
      key: {
        key: 'Attachment',
        type: EIremboFormlyFieldTypes.customfileupload,
        props: {
          required: false,
          label: 'File Attachment',
          minimumUploadSize: 100,
          maximumUploadSize: 400,
          placeholder: 'Select file to upload',
          allowedFormats: [],
        },
        validation: {
          messages: {
            minimumUploadSize: ValidationTypeDefaultMessage(
              EIremboFormlyValidationTypes.minimumUploadSize
            ),
            maximumUploadSize: ValidationTypeDefaultMessage(
              EIremboFormlyValidationTypes.maximumUploadSize
            ),
          },
        },
      },
    },
    {
      label: 'Checkbox',
      key: {
        key: 'Checkbox',
        type: EIremboFormlyFieldTypes.checkbox,
        props: {
          label: 'Checkbox',
          default: false,
          pattern: 'true',
          disabled: false,
          required: false,
          placeholder: 'Checkbox',
          hideLabel: true,
          description: '',
          indeterminate: false,
        },
      },
    },
    {
      label: 'Date Picker',
      key: {
        key: 'datepicker',
        type: EIremboFormlyFieldTypes.customdatepicker,
        props: {
          label: 'Date Picker',
          disabled: false,
          required: false,
          placeholder: 'Date Picker',
        },
      },
    },
    {
      label: 'Dropdown Selector',
      key: {
        key: 'DropdownSelector',
        type: EIremboFormlyFieldTypes.customdropdown,
        props: {
          label: 'Dropdown Selector',
          disabled: false,
          required: false,
          placeholder: 'Dropdown Selector',
          options: [
            { value: 'option1Key', label: 'Option 1' },
            { value: 'option2Key', label: 'Option 2' },
            { value: 'option3Key', label: 'Option 3' },
            { value: 'option4Key', label: 'Option 4' },
          ],
          multiple: false,
          bindValue: 'value',
          bindLabel: 'label',
        },
      },
    },
    {
      label: 'Dropdown Selector With Data Fetch',
      key: {
        key: 'DropdownSelectorWithDataFetch',
        type: EIremboFormlyFieldTypes.customdropdown,
        name: 'DropdownSelectorWithDataFetch',
        props: {
          label: 'Dropdown Selector With Data Fetch',
          disabled: false,
          required: false,
          placeholder: 'Dropdown Selector With Data Fetch',
          options: [],
          multiple: false,
          virtualScroll: true,
          useBaseUrl: true,
          dataset: {
            url: '/integration/v1/fetch/sync',
          },
          subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH,
        },
      },
    },
    {
      label: 'Dropdown Selector With Dataset Non Paginated',
      key: {
        key: 'DropdownSelectorWithDatasetNonPaginated',
        type: EIremboFormlyFieldTypes.customdropdown,
        name: 'DropdownSelectorWithDatasetNonPaginated',
        props: {
          label: 'Dataset Non Paginated',
          disabled: false,
          required: true,
          placeholder: 'Select dataset',
          options: [],
          multiple: false,
          useBaseUrl: true,
          dataset: {
            url: '/admin/v1/datasets/all',
            dataField: 'data',
          },
          subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        },
      },
    },
    {
      label: 'Dropdown Selector With Dataset Paginated',
      key: {
        key: 'DropdownSelectorWithDataset',
        type: EIremboFormlyFieldTypes.customdropdownpaginated,
        name: 'DropdownSelectorWithDataset',
        props: {
          label: 'Dataset',
          disabled: false,
          required: true,
          placeholder: 'Select dataset',
          options: [],
          multiple: false,
          useBaseUrl: true,
          dataset: {
            url: '/admin/v1/dataset-items',
            dataField: 'data.content',
          },
          subType: EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
        },
      },
    },
    {
      label: 'Email Address',
      key: {
        key: 'EmailAddress',
        type: 'input',
        props: {
          label: 'Email address',
          disabled: false,
          required: false,
          placeholder: 'Provide email address',
          hint: '',
          tooltip: '',
          internalDescription: '',
          readonly: false,
          type: 'customemail',
        },
        validation: {
          messages: {
            email: ValidationTypeDefaultMessage(
              EIremboFormlyValidationTypes.email
            ),
          },
        },
        validators: {
          validation: ['email'],
        },
      },
    },
    {
      label: 'Error Component',
      key: {
        key: 'ErrorComponent',
        type: 'customerrorcomponent',
        props: {
          layout: 'inline',
          errorKey: 'genericDataFetch',
          internalDescription: 'Internal Description: Error Component',
        },
      },
    },
    {
      label: 'International Phone No.',
      key: {
        key: 'InternationalPhoneNumber',
        type: EIremboFormlyFieldTypes.customphonenumber,
        props: {
          label: 'Phone Number International',
          required: true,
          id: 'telephone',
          countryNoValidation: true,
          preferredCountries: [],
        },
        validation: {
          messages: {
            required: 'Field is required',
            invalidNumber: 'Provided number is invalid',
            invalidNumberFormat: 'Number format is invalid',
          },
        },
        validators: {
          validation: ['invalidNumberFormat'],
        },
      },
    },

    {
      label: 'Multi-checkbox',
      key: {
        key: 'MultiCheckbox',
        type: EIremboFormlyFieldTypes.multicheckbox,
        props: {
          type: 'array',
          label: 'Multi-Checkbox',
          disabled: false,
          required: false,
          placeholder: 'Multi-Checkbox',
          options: [
            { value: 'CheckBox1Key', label: 'CheckBox 1' },
            { value: 'CheckBox2Key', label: 'CheckBox 2' },
            { value: 'CheckBox3Key', label: 'CheckBox 3' },
            { value: 'CheckBox4Key', label: 'CheckBox 4' },
          ],
        },
      },
    },

    {
      label: 'Number Input',
      key: {
        key: 'NumberInput',
        type: EIremboFormlyFieldTypes.input,
        props: {
          type: 'number',
          label: 'Number Input',
          disabled: false,
          required: false,
          placeholder: 'Number Input',
        },
      },
    },
    {
      label: 'Radio Input',
      key: {
        key: 'TextInput',
        type: EIremboFormlyFieldTypes.radio,
        props: {
          label: 'Radio Input',
          disabled: false,
          required: false,
          placeholder: 'Radio Input',
          options: [
            { value: 'Radio1Key', label: 'Radio 1' },
            { value: 'Radio2Key', label: 'Radio 2' },
            { value: 'Radio3Key', label: 'Radio 3' },
            { value: 'Radio4Key', label: 'Radio 4' },
          ],
        },
      },
    },
    {
      label: 'Section Message With Link',
      key: {
        key: 'SectionMessage',
        type: EIremboFormlyFieldTypes.customsectionmessagewithlink,
        props: {
          links: [
            {
              url: 'http://google.com',
              name: 'Google Link',
              selfTarget: true,
            },
            {
              url: 'http://facebook.com',
              name: 'Facebook',
            },
          ],
          title: 'GuidelInes',
          label: `new_section_${this.randomKey}`,
          hideLabel: true,
          message: 'This is a message about this section',
          messageType: 'warning',
          extraMessage:
            '<ol><li>Download the form here</li><li>Download the form here</li></ol>',
          extraMessageAsHtml: true,
        },
      },
    },
    {
      label: 'Text Area',
      key: {
        key: 'TextArea',
        type: EIremboFormlyFieldTypes.textarea,
        props: {
          label: 'Text Area',
          disabled: false,
          required: false,
          placeholder: 'Text Area',
          rows: 3,
        },
      },
    },
    {
      label: 'Text Input',
      key: {
        key: 'TextInput',
        type: EIremboFormlyFieldTypes.input,
        props: {
          label: 'Text Input',
          disabled: false,
          required: false,
          placeholder: 'Text Input',
        },
      },
    },
    {
      label: 'Repeatable Input',
      key: {
        key: 'subjects',
        type: EIremboFormlyFieldTypes.customrepeater,
        props: {
          addText: 'Add subject',
          uniqueRepeaterValues: ['subject_name'],
        },
        fieldArray: {
          fieldGroup: [
            {
              key: 'subject_name',
              type: 'input',
              props: {
                label: 'Subject',
                required: true,
                placeholder: 'Enter subject',
              },
            },
            {
              key: 'grade',
              type: 'input',
              props: {
                label: 'Grade',
                required: true,
                placeholder: 'Enter grade',
              },
            },
          ],
        },
      },
    },
    {
      label: 'Time Picker',
      key: {
        key: 'Timepicker',
        type: EIremboFormlyFieldTypes.customtimepicker,
        props: {
          label: 'Time Picker',
          disabled: false,
          required: false,
          placeholder: 'Time Picker',
          startHour: 8,
          endHour: 16,
          interval: 30,
        },
      },
    },
    {
      label: 'Table',
      key: {
        key: 'Table',
        type: EIremboFormlyFieldTypes.customdatasettable,
        props: {
          hint: '',
          label: 'Dataset table from details',
          columns: [
            {
              key: 'name',
              label: 'Type',
              sortable: true,
            },
            {
              key: 'value',
              label: 'Type',
              sortable: true,
            },
            {
              key: 'details.parent',
              label: 'Description',
              sortable: true,
            },
          ],
          dataset: {
            url: '/admin/v1/dataset-items',
            params: {
              page: 0,
              size: 10,
            },
            dataField: 'data.content',
            searching: false,
          },
          tooltip: '',
          disabled: false,
          internal: true,
          pageSizes: [10, 50, 100],
          tableClass: 'table',
          useBaseUrl: true,
          placeholder: '',
          isSelectable: true,
          roundedTable: true,
          showDropdown: false,
          isSortVisible: false,
          maxSelectable: 1,
          isSearchVisible: false,
          paginationVisible: false,
          searchPlaceholder: 'Search datasets',
          internalDescription: '',
          selectionOutputPath: '',
          paginationInsideTable: false,
        },
        defaultValue: null,
        expressions: {},
        validation: {
          messages: {},
        },
      },
    },
    {
      label: 'Dynamic Repeatable Input',
      key: {
        key: 'dynamicItems',
        type: EIremboFormlyFieldTypes.customdynamicrepeater,
        props: {
          label: 'Dynamic Items',
          dynamicRepeaterConfig: {
            itemIdField: 'id',
            itemLabelField: 'label',
            emptyStateMessage:
              'No items available. Items will be populated automatically from the data source.',
            enableDebugLogging: false,
          },
        },
        fieldArray: {
          fieldGroup: [
            {
              key: 'fileUpload',
              type: EIremboFormlyFieldTypes.customfileupload,
              props: {
                required: true,
                label: '{{itemLabel}}',
                minimumUploadSize: 100,
                maximumUploadSize: 400,
                placeholder: 'Select file to upload',
                allowedFormats: [],
                disabled: false,
                hint: 'Upload: {{label}}',
              },
              validation: {
                messages: {
                  minimumUploadSize: 'Minimum upload size not met',
                  maximumUploadSize: 'Maximum upload size exceeded',
                },
              },
            },
          ],
        },
      },
    },
    {
      label: 'Currency Format Input',
      key: {
        key: 'CurrencyFormatInput',
        type: EIremboFormlyFieldTypes.customcurrencyformatinput,
        props: {
          label: 'Currency Format Input',
          disabled: false,
          readonly: false,
          required: false,
          placeholder: 'Currency Format Input',
        },
      },
    },
  ];

  subscriptionControl$: Subject<void> = new Subject();

  connectedBlockId: string[] = [];

  formBuilderManipulatorClass!: FormBuilderManipulatorClass;

  EIremboFormlyFieldTypes = EIremboFormlyFieldTypes;

  constructor(
    private formBuilderService: FormBuilderService,
    private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector
  ) {
    this.formBuilderManipulatorClass = new FormBuilderManipulatorClass(
      this.injector
    );
  }

  ngOnInit(): void {
    this.formBuilderService.formFieldBlockIdsSet$
      .pipe(takeUntil(this.subscriptionControl$))
      .subscribe((blockIdSet: Set<string>) => {
        this.connectedBlockId = Array.from(blockIdSet);
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.subscriptionControl$.next();
    this.subscriptionControl$.complete();
  }
}
